import React, { useState } from 'react';
import axios from 'axios';
import { Send, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '../context/I18nContext';

const BACKEND_URL = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);
const API = `${BACKEND_URL}/api`;

export default function ContactSection() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error('Please fill in all fields.');
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, { ...form, lang });
      setDone(true);
      toast.success(t.contact.success);
      setForm({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setDone(false), 5000);
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-14 md:py-20 bg-white">
      <div className="zh-container">
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-14 items-start">
          {/* Left copy */}
          <div className="lg:col-span-2">
            <div className="text-[#d98c14] tracking-[0.3em] text-xs font-semibold mb-3">
              {t.contact.eyebrow}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-4">
              {t.contact.title}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6">{t.contact.body}</p>
            <div className="h-1 w-14 bg-[#f5a623] rounded-full" />
          </div>

          {/* Form */}
          <form
            onSubmit={onSubmit}
            className="lg:col-span-3 bg-white rounded-2xl shadow-[0_20px_50px_-20px_rgba(0,0,0,0.15)] p-6 md:p-8 border border-gray-100"
          >
            <div className="grid md:grid-cols-2 gap-5 mb-5">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  {t.contact.name}
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={update('name')}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-[#f5a623] focus:ring-2 focus:ring-[#f5a623]/20 transition-colors text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  {t.contact.email}
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={update('email')}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-[#f5a623] focus:ring-2 focus:ring-[#f5a623]/20 transition-colors text-sm"
                />
              </div>
            </div>
            <div className="mb-5">
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                {t.contact.subject}
              </label>
              <input
                type="text"
                value={form.subject}
                onChange={update('subject')}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-[#f5a623] focus:ring-2 focus:ring-[#f5a623]/20 transition-colors text-sm"
              />
            </div>
            <div className="mb-6">
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                {t.contact.message}
              </label>
              <textarea
                rows={5}
                value={form.message}
                onChange={update('message')}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-[#f5a623] focus:ring-2 focus:ring-[#f5a623]/20 transition-colors text-sm resize-none"
              />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <button type="submit" disabled={submitting || done} className="zh-btn-gold disabled:opacity-70 disabled:cursor-not-allowed">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                  </>
                ) : done ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" /> Sent
                  </>
                ) : (
                  <>
                    {t.contact.submit} <Send className="w-4 h-4" />
                  </>
                )}
              </button>
              {done && <p className="text-sm text-green-600 font-medium">{t.contact.success}</p>}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
